import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAuth } from "../components/contexts/AuthContext.jsx";
import { formatDate, pluralize } from "../Pages/Plan/billingFormat.js";
import { BILLING_QUERY_KEY, parseCheckoutReturn, syncBilling } from "../services/billingService.js";
import {
  checkoutExpectation,
  forgetStripeReturn,
  isReflected,
  pollUntil,
  portalExpectation,
  readStripeReturn,
} from "./stripeReturn.js";

const initialState = (searchParams) => {
  const checkout = parseCheckoutReturn(searchParams);
  // Hours have their own return (useHoursCheckoutReturn), also outside this page
  if (checkout?.kind === "hours") return { phase: null };
  if (checkout?.status === "cancel") return { phase: "cancel" };
  if (checkout?.status === "success") {
    return { phase: "confirming", expectation: checkoutExpectation(checkout, readStripeReturn()) };
  }
  const portal = searchParams.get("portal");
  if (portal === "back") return { phase: "back" };
  if (portal === "done") {
    return { phase: "confirming", expectation: portalExpectation(searchParams.get("flow"), readStripeReturn()) };
  }
  return { phase: null };
};

const successMessage = ({ kind, plan, quantity }, summary) => {
  if (kind === "plan") {
    if (summary.plan === plan) return `¡Listo! Ya tienes el plan ${summary.plan_name}.`;
    if (plan === "free") return `Tu plan pasará a Free el ${formatDate(summary.current_period_end)}.`;
    return `Tu plan pasará a ${summary.scheduled_plan_name} el ${formatDate(summary.current_period_end)}.`;
  }
  const seatsEnd = formatDate(summary.seats.current_period_end);
  if (quantity === 0) return `Tus usuarios adicionales dejarán de cobrarse el ${seatsEnd}.`;
  const seats = pluralize(quantity, "usuario adicional", "usuarios adicionales");
  if (summary.extra_users === quantity) return `¡Listo! Ya tienes ${seats}.`;
  return `Pasarás a ${seats} el ${seatsEnd}.`;
};

/**
 * Return from Checkout or a portal deep link on /ajustes/plan. Without webhooks the change only lands when the
 * backend re-reads Stripe, so it calls `sync/` until the expected change shows up, an error or the deadline.
 * `phase`: null | "cancel" | "back" | "confirming" | "done" | "timeout" | "error".
 */
export function useStripeReturn() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const [state, setState] = useState(() => initialState(searchParams));

  useEffect(() => {
    if (!state.phase) return;
    forgetStripeReturn();
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.phase !== "confirming") return undefined;
    let cancelled = false;
    const { expectation } = state;
    pollUntil({
      tick: async () => {
        const summary = await syncBilling();
        if (!cancelled) queryClient.setQueryData(BILLING_QUERY_KEY, summary);
        return summary;
      },
      // Without a known target (e.g. a portal return with no saved expectation) one sync is enough
      isDone: (summary) => !expectation || isReflected(expectation, summary),
      isCancelled: () => cancelled,
    })
      .then(async (outcome) => {
        if (!outcome) return;
        await refreshUser();
        if (cancelled) return;
        setState({ phase: outcome.status, expectation });
        if (outcome.status !== "done") return;
        if (expectation) toast.success(successMessage(expectation, outcome.result));
        else toast.info("Cambios guardados.");
      })
      .catch(() => {
        if (!cancelled) setState({ phase: "error", expectation });
      });
    return () => {
      cancelled = true;
    };
    // refreshUser is recreated on every AuthContext render; depending on it would restart the polling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const retry = useCallback(() => setState((current) => ({ ...current, phase: "confirming" })), []);
  const dismiss = useCallback(() => setState({ phase: null }), []);

  return { phase: state.phase, expectation: state.expectation, retry, dismiss };
}
